import React, { useRef, useState, useEffect, useCallback } from "react";
import ReactDOM from "react-dom";
import { motion, AnimatePresence, useReducedMotion } from "framer-motion";
import { Rnd } from "react-rnd";
import {
    FaUndo, FaRedo, FaTrash, FaCheck, FaChevronDown, FaChevronUp,
    FaImage, FaEyeSlash, FaCalculator, FaPen, FaHighlighter,
    FaEraser, FaHandPaper, FaSearchPlus, FaSearchMinus,
    FaCrosshairs
} from "react-icons/fa";
import TutorialCoachmark, { TUTORIAL_HIGHLIGHT } from "./TutorialCoachmark";
import { Capacitor } from '@capacitor/core';
import { App } from '@capacitor/app';
import { RenderMarkdownWithLatex } from "../../utils/utils";
import { useAndroidSafeArea } from "../../hooks/useAndroidSafeArea";
import { getSheetBackdropMotion, getSheetPanelMotion, useSheetSlideIn } from "../../utils/sheetMotion";
import doodlePattern from "../../img/grid.svg";
// ==========================================
// CONSTANTS
// ==========================================
const TOOLS = {
    PEN: 'pen',
    HIGHLIGHTER: 'highlighter',
    ERASER: 'eraser',
    MOVE: 'move',
};
const PEN_COLORS = [
    '#1a1a1a',  // Czarny
    '#1565C0',  // Niebieski
    '#C62828',  // Czerwony
    '#2E7D32',  // Zielony
    '#6A1B9A',  // Fioletowy
];
const HIGHLIGHTER_COLORS = [
    '#FFEE58',  // Żółty
    '#69F0AE',  // Zielony
    '#FF80AB',  // Różowy
    '#80D8FF',  // Niebieski
];
const PEN_WIDTHS = [2, 4, 7];
const HIGHLIGHTER_WIDTH = 18;
const HIGHLIGHTER_OPACITY = 0.35;
const ERASER_RADIUS = 12;
const MAX_UNDO_STEPS = 25;
const MIN_ZOOM = 0.5;
const MAX_ZOOM = 4;
const ZOOM_STEP = 0.25;
// Line straightening: trzymaj rysik nieruchomo na końcu kreski
const LINE_STRAIGHTEN_HOLD_MS = 450;
const LINE_STRAIGHTEN_STILL_RADIUS = 6; // px w paper space — max ruch żeby uznać za "nieruchomo"
const LINE_STRAIGHTEN_MIN_LENGTH_PX = 60; // krótszych kresek nie prostujemy (drobne pismo)
// Prostujemy TYLKO to, co i tak było prawie proste. Bez tego pismo odręczne
// (pauza przy zakręcie litery) zamieniało się w kreskę.
const LINE_STRAIGHTEN_MAX_DEVIATION_RATIO = 0.10;
const LINE_STRAIGHTEN_MAX_DEVIATION_PX = 16;
// Minimalny odstęp próbek w px ekranu (skalowany zoomem). Rysik daje 240 próbek/s,
// więc przy progu ~0.3px zapisywaliśmy szum digitizera 1:1 i wygładzanie krzywymi
// nic nie dawało (odcinki były podpikselowe) — drobne cyfry wychodziły poszarpane.
// Koniec kreski i tak dokłada addFinalPoint, więc krótkie kreski nie ucierpią.
const MIN_POINT_DIST_PX = 1.2;
// Wygładzanie wejścia: nowy punkt to prev + beta*(surowy - prev). Mniejsza beta =
// gładziej, ale grot bardziej „ucieka" kresce. 0.5 to typowy kompromis.
const INPUT_SMOOTHING_BETA = 0.5;
// Palm rejection BEZ mierzenia plamy kontaktu — e.width/height na iPadzie potrafi
// odrzucić normalny palec. Dłoń poznajemy po tym, że:
//  * nie rysuje (po wykryciu rysika dotyk nigdy nie rysuje),
//  * nie tworzy pary do gestu (leży od dawna, nie ląduje razem z drugim palcem),
//  * nie rusza płótnem (gest aktywuje dopiero realny ruch kontaktu).
// Dwa palce szczypnięcia lądują niemal równocześnie — to je odróżnia od dłoni.
const GESTURE_PAIR_WINDOW_MS = 300;
// Uzbrojony gest rusza płótnem dopiero po tylu px ruchu — nieruchoma dłoń nie liczy się
const GESTURE_ACTIVATE_PX = 8;
// Po tylu ms bez żadnego zdarzenia rysika uznajemy, że stan „rysik rysuje" jest
// zwietrzały (zgubiony pointerup) i nie wolno nim dłużej blokować dotyku.
const PEN_ACTIVE_TIMEOUT_MS = 400;
// ==========================================
// HELPER: Rysowanie pojedynczego stroke'a
// ==========================================
const drawStroke = (ctx, stroke) => {
    const pts = stroke.points;
    if (!pts || pts.length < 2) {
        if (pts && pts.length === 1) {
            ctx.beginPath();
            ctx.arc(pts[0].x, pts[0].y, stroke.width / 2, 0, Math.PI * 2);
            ctx.fillStyle = stroke.tool === TOOLS.ERASER ? '#000' : stroke.color;
            ctx.globalAlpha = stroke.tool === TOOLS.ERASER ? 1 : (stroke.opacity || 1);
            ctx.globalCompositeOperation = stroke.tool === TOOLS.ERASER ? 'destination-out' : 'source-over';
            ctx.fill();
        }
        return;
    }
    ctx.save();
    if (stroke.tool === TOOLS.ERASER) {
        ctx.globalCompositeOperation = 'destination-out';
        ctx.globalAlpha = 1;
        ctx.strokeStyle = '#000';
    } else {
        ctx.globalCompositeOperation = 'source-over';
        ctx.globalAlpha = stroke.opacity || 1;
        ctx.strokeStyle = stroke.color;
    }
    ctx.lineWidth = stroke.width;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.beginPath();
    ctx.moveTo(pts[0].x, pts[0].y);
    if (stroke.straightLine) {
        const last = pts[pts.length - 1];
        ctx.lineTo(last.x, last.y);
    } else {
        for (let i = 1; i < pts.length - 1; i++) {
            const midX = (pts[i].x + pts[i + 1].x) / 2;
            const midY = (pts[i].y + pts[i + 1].y) / 2;
            ctx.quadraticCurveTo(pts[i].x, pts[i].y, midX, midY);
        }
        const last = pts[pts.length - 1];
        ctx.lineTo(last.x, last.y);
    }
    ctx.stroke();
    ctx.restore();
};
// ==========================================
// HELPER: Generuj układ współrzędnych
// ==========================================
const generateCoordinateSystem = (centerX, centerY, size, strokeWidth) => {
    const strokes = [];
    const axisColor = '#555555';
    const tickColor = '#999999';
    const halfSize = size / 2;
    const tickSpacing = size / 10;
    const tickLen = 4;
    const arrowSize = 8;
    const line = (x1, y1, x2, y2, color, width, opacity = 1) => ({
        tool: TOOLS.PEN, color, width, opacity, straightLine: true,
        points: [{ x: x1, y: y1 }, { x: x2, y: y2 }],
    });
    strokes.push(line(centerX - halfSize, centerY, centerX + halfSize, centerY, axisColor, strokeWidth));
    strokes.push(line(centerX + halfSize - arrowSize, centerY - 5, centerX + halfSize, centerY, axisColor, strokeWidth));
    strokes.push(line(centerX + halfSize - arrowSize, centerY + 5, centerX + halfSize, centerY, axisColor, strokeWidth));
    strokes.push(line(centerX, centerY + halfSize, centerX, centerY - halfSize, axisColor, strokeWidth));
    strokes.push(line(centerX - 5, centerY - halfSize + arrowSize, centerX, centerY - halfSize, axisColor, strokeWidth));
    strokes.push(line(centerX + 5, centerY - halfSize + arrowSize, centerX, centerY - halfSize, axisColor, strokeWidth));
    for (let i = -4; i <= 4; i++) {
        if (i === 0) continue;
        const x = centerX + i * tickSpacing;
        strokes.push(line(x, centerY - tickLen, x, centerY + tickLen, tickColor, strokeWidth * 0.6, 0.7));
    }
    for (let i = -4; i <= 4; i++) {
        if (i === 0) continue;
        const y = centerY + i * tickSpacing;
        strokes.push(line(centerX - tickLen, y, centerX + tickLen, y, tickColor, strokeWidth * 0.6, 0.7));
    }
    return strokes;
};
// ==========================================
// SUB-COMPONENT: SIMPLE CALCULATOR
// ==========================================
const SimpleCalculator = ({ onClose, style, className }) => {
    const [display, setDisplay] = useState("0");
    const [prevValue, setPrevValue] = useState(null);
    const [operator, setOperator] = useState(null);
    const [waitingForOperand, setWaitingForOperand] = useState(false);
    const inputDigit = (digit) => {
        if (waitingForOperand) { setDisplay(String(digit)); setWaitingForOperand(false); }
        else { setDisplay(display === "0" ? String(digit) : display + digit); }
    };
    const inputDot = () => {
        if (waitingForOperand) { setDisplay("0."); setWaitingForOperand(false); }
        else if (display.indexOf(".") === -1) { setDisplay(display + "."); }
    };
    const clearDisplay = () => { setDisplay("0"); setPrevValue(null); setOperator(null); setWaitingForOperand(false); };
    const calculate = (prev, next, op) => {
        switch (op) {
            case "+": return prev + next; case "-": return prev - next;
            case "*": return prev * next; case "/": return next === 0 ? "Error" : prev / next;
            default: return next;
        }
    };
    const performOperation = (nextOp) => {
        const val = parseFloat(display);
        if (prevValue === null) { setPrevValue(val); }
        else if (operator) {
            const result = calculate(prevValue || 0, val, operator);
            setPrevValue(result); setDisplay(String(result).slice(0, 12));
        }
        setWaitingForOperand(true); setOperator(nextOp);
    };
    const handleSqrt = () => { const v = parseFloat(display); setDisplay(v < 0 ? "Error" : String(Math.sqrt(v)).slice(0, 12)); setWaitingForOperand(true); };
    const handlePercent = () => { setDisplay(String(parseFloat(display) / 100).slice(0, 12)); setWaitingForOperand(true); };
    const btnBase = "rounded font-bold shadow active:scale-95 transition-transform";
    return (
        <div className={`bg-[#2d3748] p-2 flex flex-col h-full w-full rounded-lg shadow-[0_10px_40px_rgba(0,0,0,0.5)] border border-gray-600 select-none ${className}`}
            style={style} onPointerDown={e => e.stopPropagation()} onTouchStart={e => e.stopPropagation()}>
            <div className="drag-handle h-7 bg-gray-700 hover:bg-gray-600 transition-colors rounded-t mb-1.5 flex justify-center items-center cursor-move shrink-0">
                <div className="w-10 h-1 bg-gray-500 rounded-full" />
            </div>
            <div className="bg-[#A8B79A] text-right font-mono text-2xl px-2 py-1 mb-2 rounded text-black tracking-widest shadow-inner h-12 flex items-center justify-end overflow-hidden shrink-0">{display}</div>
            <div className="grid grid-cols-4 gap-1.5 flex-grow">
                <button onClick={clearDisplay} className={`col-span-1 bg-red-500 hover:bg-red-600 text-white text-sm ${btnBase}`}>C</button>
                <button onClick={handleSqrt} className={`bg-gray-600 hover:bg-gray-500 text-white text-sm ${btnBase}`}>√</button>
                <button onClick={handlePercent} className={`bg-gray-600 hover:bg-gray-500 text-white text-sm ${btnBase}`}>%</button>
                <button onClick={() => performOperation("/")} className={`bg-orange-500 hover:bg-orange-600 text-white text-lg ${btnBase}`}>÷</button>
                {[7,8,9].map(d => <button key={d} onClick={() => inputDigit(d)} className={`bg-gray-700 hover:bg-gray-600 text-white text-xl ${btnBase}`}>{d}</button>)}
                <button onClick={() => performOperation("*")} className={`bg-orange-500 hover:bg-orange-600 text-white text-lg ${btnBase}`}>×</button>
                {[4,5,6].map(d => <button key={d} onClick={() => inputDigit(d)} className={`bg-gray-700 hover:bg-gray-600 text-white text-xl ${btnBase}`}>{d}</button>)}
                <button onClick={() => performOperation("-")} className={`bg-orange-500 hover:bg-orange-600 text-white text-lg ${btnBase}`}>−</button>
                {[1,2,3].map(d => <button key={d} onClick={() => inputDigit(d)} className={`bg-gray-700 hover:bg-gray-600 text-white text-xl ${btnBase}`}>{d}</button>)}
                <button onClick={() => performOperation("+")} className={`bg-orange-500 hover:bg-orange-600 text-white text-lg ${btnBase}`}>+</button>
                <button onClick={() => inputDigit(0)} className={`col-span-2 bg-gray-700 hover:bg-gray-600 text-white text-xl ${btnBase}`}>0</button>
                <button onClick={inputDot} className={`bg-gray-700 hover:bg-gray-600 text-white text-xl ${btnBase}`}>.</button>
                <button onClick={() => performOperation("=")} className={`bg-orange-500 hover:bg-orange-600 text-white text-lg ${btnBase}`}>=</button>
            </div>
            <button onClick={onClose} className="mt-2 text-[10px] text-gray-400 text-center w-full uppercase tracking-wider hover:text-white shrink-0">Zamknij kalkulator</button>
        </div>
    );
};
// ==========================================
// DRAWING TOOLBAR
// ==========================================
const DrawingToolbar = ({
    activeTool, setActiveTool,
    activeColor, setActiveColor,
    penWidth, setPenWidth,
    zoom, onZoomIn, onZoomOut,
    onUndo, onRedo, onClear,
    canUndo, canRedo,
    onInsertCoordSystem,
    paddingTop, onSave,
    showCalculator, setShowCalculator,
    showBgImage, setShowBgImage, hasImage,
    onDebugTap, highlightSave,
}) => {
    const [colorPickerOpen, setColorPickerOpen] = useState(false);
    const colorBtnRef = useRef(null);
    const colors = activeTool === TOOLS.HIGHLIGHTER ? HIGHLIGHTER_COLORS : PEN_COLORS;
    const effectiveColor = activeTool === TOOLS.HIGHLIGHTER
        ? (HIGHLIGHTER_COLORS.includes(activeColor) ? activeColor : HIGHLIGHTER_COLORS[0])
        : (PEN_COLORS.includes(activeColor) ? activeColor : PEN_COLORS[0]);
    useEffect(() => {
        if (!colorPickerOpen) return;
        const close = (e) => {
            if (colorBtnRef.current && !colorBtnRef.current.contains(e.target)) setColorPickerOpen(false);
        };
        document.addEventListener('pointerdown', close);
        return () => document.removeEventListener('pointerdown', close);
    }, [colorPickerOpen]);
    useEffect(() => { setColorPickerOpen(false); }, [activeTool]);
    const isDrawingTool = activeTool === TOOLS.PEN || activeTool === TOOLS.HIGHLIGHTER;
    return (
        <div className="bg-white shrink-0 z-40 border-b border-gray-100" style={{ paddingTop }}>
            {/* RZAD 1: narzedzia + kolor + historia + ZAPISZ */}
            <div className="flex items-center gap-0.5 px-2 pt-1 pb-0.5">
                <ToolButton icon={<FaPen size={13} />} active={activeTool === TOOLS.PEN}
                    onClick={() => setActiveTool(TOOLS.PEN)}
                    indicatorColor={activeTool === TOOLS.PEN ? effectiveColor : null} title="Długopis" />
                <ToolButton icon={<FaHighlighter size={13} />} active={activeTool === TOOLS.HIGHLIGHTER}
                    onClick={() => setActiveTool(TOOLS.HIGHLIGHTER)}
                    indicatorColor={activeTool === TOOLS.HIGHLIGHTER ? effectiveColor : null} title="Zakreślacz" />
                <ToolButton icon={<FaEraser size={13} />} active={activeTool === TOOLS.ERASER}
                    onClick={() => setActiveTool(TOOLS.ERASER)} title="Gumka" />
                <ToolButton icon={<FaHandPaper size={13} />} active={activeTool === TOOLS.MOVE}
                    onClick={() => setActiveTool(TOOLS.MOVE)} title="Przesuń" />
                {isDrawingTool && (
                    <div className="relative shrink-0 flex items-center justify-center w-9 h-9" ref={colorBtnRef}>
                        <button onClick={() => setColorPickerOpen(!colorPickerOpen)} aria-label="Wybierz kolor"
                            className={`w-6 h-6 rounded-full border-2 border-white ring-1 transition-all active:scale-90 shadow-sm ${
                                colorPickerOpen ? 'ring-[#233855] scale-110' : 'ring-gray-300 hover:ring-[#233855]'
                            }`}
                            style={{ backgroundColor: effectiveColor }} />
                        {colorPickerOpen && (
                            <div className="absolute top-full left-1/2 -translate-x-1/2 mt-2 bg-white rounded-xl shadow-[0_8px_30px_rgba(0,0,0,0.15)] border border-gray-200 p-2.5 z-[100]"
                                onPointerDown={e => e.stopPropagation()}>
                                <div className="flex gap-2">
                                    {colors.map(c => (
                                        <button key={c} aria-label={`Kolor ${c}`}
                                            onClick={() => { setActiveColor(c); setColorPickerOpen(false); }}
                                            className={`w-9 h-9 rounded-full border-2 transition-all active:scale-90 ${
                                                effectiveColor === c ? 'border-[#233855] scale-110 shadow-md' : 'border-gray-200 hover:border-gray-400'
                                            }`}
                                            style={{ backgroundColor: c }} />
                                    ))}
                                </div>
                                <div className="absolute -top-1.5 left-1/2 -translate-x-1/2 w-3 h-3 bg-white rotate-45 border-l border-t border-gray-200" />
                            </div>
                        )}
                    </div>
                )}
                <div className="w-px h-5 bg-gray-200 mx-1 shrink-0" />
                <ToolButton icon={<FaUndo size={12} />} onClick={onUndo} disabled={!canUndo} title="Cofnij" />
                <ToolButton icon={<FaRedo size={12} />} onClick={onRedo} disabled={!canRedo} title="Ponów" />
                <div className="flex-grow" />
                <button onClick={onSave} data-tut="scratchpad-save"
                    className={`bg-[#233855] text-white pl-3 pr-2.5 py-1.5 rounded-lg font-bold text-[11px] tracking-widest shadow-sm hover:bg-[#345081] flex items-center gap-1 active:scale-95 transition-all shrink-0 ${highlightSave ? TUTORIAL_HIGHLIGHT : ""}`}>
                    ZAPISZ <FaCheck size={10} />
                </button>
            </div>
            {/* RZAD 2: kalkulator, obraz, zoom, grubosc, cwiartka, wyczysc */}
            <div className="flex items-center gap-1 px-2 pb-1 pt-0.5 border-t border-gray-100 flex-wrap">
                <ToolButton icon={<FaCalculator size={13} />} active={showCalculator}
                    onClick={() => setShowCalculator(!showCalculator)} title="Kalkulator" />
                {hasImage && (
                    <ToolButton icon={showBgImage ? <FaImage size={13} /> : <FaEyeSlash size={13} />}
                        active={showBgImage} onClick={() => setShowBgImage(!showBgImage)} title="Podkład zadania" />
                )}
                <div className="w-px h-6 bg-gray-200 mx-0.5 shrink-0" />
                <ToolButton icon={<FaSearchMinus size={12} />} onClick={onZoomOut} disabled={zoom <= MIN_ZOOM} title="Oddal" />
                {/* 5 szybkich tapnięć = podgląd zdarzeń rysika (diagnostyka) */}
                <span onClick={onDebugTap}
                    className="text-[11px] text-gray-600 font-mono tabular-nums w-9 text-center shrink-0 select-none">
                    {Math.round(zoom * 100)}%
                </span>
                <ToolButton icon={<FaSearchPlus size={12} />} onClick={onZoomIn} disabled={zoom >= MAX_ZOOM} title="Przybliż" />
                {activeTool === TOOLS.PEN && (
                    <>
                        <div className="w-px h-6 bg-gray-200 mx-0.5 shrink-0" />
                        {PEN_WIDTHS.map(w => (
                            <button key={w} onClick={() => setPenWidth(w)} aria-label={`Grubość ${w}`}
                                className={`shrink-0 flex items-center justify-center w-9 h-9 rounded-xl transition-all active:scale-90 ${
                                    penWidth === w ? 'bg-[#233855]/10' : 'hover:bg-gray-100'
                                }`}>
                                <span className="rounded-full"
                                    style={{ width: w * 2.5 + 5, height: w * 2.5 + 5, backgroundColor: penWidth === w ? '#233855' : '#9ca3af' }} />
                            </button>
                        ))}
                    </>
                )}
                <div className="w-px h-6 bg-gray-200 mx-0.5 shrink-0" />
                <ToolButton icon={<FaCrosshairs size={13} />} onClick={onInsertCoordSystem}
                    title="Wstaw układ współrzędnych" />
                <div className="flex-grow" />
                <ToolButton icon={<FaTrash size={12} />} onClick={onClear}
                    className="text-red-500 hover:text-red-600 hover:bg-red-50" title="Wyczyść wszystko" />
            </div>
        </div>
    );
};
// --- Tool button ---
const ToolButton = ({ icon, active, onClick, disabled, className = '', indicatorColor, title }) => (
    <button onClick={onClick} disabled={disabled} title={title} aria-label={title} aria-pressed={active}
        className={`
            relative w-9 h-9 flex items-center justify-center rounded-lg shrink-0 transition-all duration-150 active:scale-90
            ${active ? 'bg-[#233855] text-white shadow-sm' : 'text-gray-500 hover:text-[#233855] hover:bg-gray-100'}
            ${disabled ? 'opacity-40 pointer-events-none' : ''}
            ${className}
        `}>
        {icon}
        {indicatorColor && (
            <span className="absolute -bottom-0.5 left-1/2 -translate-x-1/2 w-3.5 h-1 rounded-full ring-1 ring-white/70"
                style={{ backgroundColor: indicatorColor }} />
        )}
    </button>
);
// ==========================================
// MAIN COMPONENT: SCRATCHPAD MODAL
// ==========================================
const ScratchpadModal = ({
    isOpen, onClose, problem,
    initialPaths, onSave,
    // coach — dymek samouczka { title, body, stepIndex, stepCount }.
    // Brudnopis zaslania caly ekran, wiec krok "brudnopis" musi byc prowadzony
    // w srodku: ten sam dymek i to samo podswietlenie co na ekranie zadania.
    coach,
}) => {
    // --- REFS ---
    const canvasRef = useRef(null);
    const containerRef = useRef(null);
    const strokesRef = useRef([]);
    const currentStrokeRef = useRef(null);
    const undoStackRef = useRef([]);
    const redoStackRef = useRef([]);
    const zoomRef = useRef(1);
    const offsetRef = useRef({ x: 0, y: 0 });
    const rafRef = useRef(null);
    const canvasRectRef = useRef(null);
    // === OFFSCREEN CANVAS CACHE (PERFORMANCE FIX) ===
    const offscreenRef = useRef(null);
    const offscreenDirtyRef = useRef(true);
    // Zapamiętujemy transform przy którym renderowaliśmy offscreen
    const offscreenTransformRef = useRef({ zoom: 1, offsetX: 0, offsetY: 0, width: 0, height: 0 });
    // Pointer tracking — dokładnie JEDEN aktywny wskaźnik rysujący/przesuwający.
    // Zdarzenia z innego pointerId nigdy nie ruszają bieżącej kreski.
    const activePointerRef = useRef(null);   // { id, type }
    const panRef = useRef(null);             // { startX, startY, offset }
    const touchPointersRef = useRef(new Map());
    const gestureRef = useRef(null);         // stan gestu dwoma palcami
    const stylusDetectedRef = useRef(false);
    // Czy ta przeglądarka w ogóle raportuje nacisk grotu w e.buttons. Dopóki nie
    // zobaczymy buttons&1, nie ufamy buttons===0 (inaczej platforma, która tego
    // nie wypełnia, ucinałaby każdą kreskę po pierwszym ruchu).
    const penContactSeenRef = useRef(false);
    const penUpTimeRef = useRef(-Infinity);      // timeStamp ostatniego oderwania rysika
    const lastPenEventTimeRef = useRef(-Infinity); // timeStamp DOWOLNEGO zdarzenia rysika
    const activeStartTimeRef = useRef(-Infinity); // timeStamp startu bieżącego wskaźnika
    // Line straightening
    const holdTimerRef = useRef(null);
    const lastMovePointRef = useRef(null);
    // Color memory per tool (persisted in localStorage)
    const lastPenColorRef = useRef((() => {
        const c = localStorage.getItem('scratchpad_pen_color');
        return PEN_COLORS.includes(c) ? c : PEN_COLORS[0];
    })());
    const lastHighlighterColorRef = useRef((() => {
        const c = localStorage.getItem('scratchpad_highlighter_color');
        return HIGHLIGHTER_COLORS.includes(c) ? c : HIGHLIGHTER_COLORS[0];
    })());
    // --- STATE ---
    const [activeTool, setActiveToolRaw] = useState(() => {
        const t = localStorage.getItem('scratchpad_tool');
        return Object.values(TOOLS).includes(t) ? t : TOOLS.PEN;
    });
    const [activeColor, setActiveColor] = useState(() => {
        const c = localStorage.getItem('scratchpad_color');
        return [...PEN_COLORS, ...HIGHLIGHTER_COLORS].includes(c) ? c : PEN_COLORS[0];
    });
    const [penWidth, setPenWidth] = useState(() => {
        const w = parseInt(localStorage.getItem('scratchpad_width'));
        return PEN_WIDTHS.includes(w) ? w : PEN_WIDTHS[1];
    });
    const setActiveTool = useCallback((tool) => {
        if (activeTool === TOOLS.PEN) {
            lastPenColorRef.current = activeColor;
            localStorage.setItem('scratchpad_pen_color', activeColor);
        }
        if (activeTool === TOOLS.HIGHLIGHTER) {
            lastHighlighterColorRef.current = activeColor;
            localStorage.setItem('scratchpad_highlighter_color', activeColor);
        }
        if (tool === TOOLS.PEN) setActiveColor(lastPenColorRef.current);
        if (tool === TOOLS.HIGHLIGHTER) setActiveColor(lastHighlighterColorRef.current);
        setActiveToolRaw(tool);
    }, [activeTool, activeColor]);
    useEffect(() => {
        localStorage.setItem('scratchpad_tool', activeTool);
        localStorage.setItem('scratchpad_color', activeColor);
        localStorage.setItem('scratchpad_width', String(penWidth));
    }, [activeTool, activeColor, penWidth]);
    // Handlery pointerów siedzą na natywnych listenerach i muszą mieć stabilną
    // tożsamość — ustawienia narzędzia czytamy przez ref, nie przez domknięcie.
    const toolRef = useRef({ activeTool, activeColor, penWidth });
    toolRef.current = { activeTool, activeColor, penWidth };
    // Element canvasa trzymamy też w stanie — dzięki temu listenery podpinają się
    // dokładnie wtedy, gdy element istnieje, bez zgadywania kolejności efektów.
    const [canvasEl, setCanvasEl] = useState(null);
    const setCanvasRef = useCallback((el) => {
        canvasRef.current = el;
        setCanvasEl(el);
    }, []);
    const [zoom, setZoom] = useState(1);
    const [canUndo, setCanUndo] = useState(false);
    const [canRedo, setCanRedo] = useState(false);
    const [showFormulas, setShowFormulas] = useState(false);
    const [showCalculator, setShowCalculator] = useState(false);
    const [showBgImage, setShowBgImage] = useState(true);
    const { paddingTop, paddingBottom } = useAndroidSafeArea();
    const prefersReducedMotion = useReducedMotion();
    const slideIn = useSheetSlideIn(isOpen);

    // ==========================================
    // HELPER: Invalidate offscreen cache
    // ==========================================
    const invalidateOffscreen = useCallback(() => {
        offscreenDirtyRef.current = true;
    }, []);

    // ==========================================
    // HELPER: Inkrementalny append stroku do offscreen
    // Zamiast full rebuild (O(n)) → dorysuuj tylko 1 stroke (O(1))
    // Używany tylko przy commitcie nowego stroku gdy transform jest stabilny.
    // We wszystkich innych przypadkach (undo/redo/clear/zoom/pan) używamy invalidateOffscreen.
    // ==========================================
    const appendStrokeToOffscreen = useCallback((stroke) => {
        const canvas = canvasRef.current;
        if (!offscreenRef.current || !canvas) {
            offscreenDirtyRef.current = true;
            return;
        }
        const oc = offscreenRef.current;
        if (oc.width !== canvas.width || oc.height !== canvas.height || offscreenDirtyRef.current) {
            offscreenDirtyRef.current = true;
            return;
        }
        // Cache trzyma transform sprzed ewentualnego zoomu/pana — doklejenie
        // przy innym transformie wsadziłoby kreskę w złe miejsce.
        const t = offscreenTransformRef.current;
        if (t.zoom !== zoomRef.current || t.offsetX !== offsetRef.current.x || t.offsetY !== offsetRef.current.y) {
            offscreenDirtyRef.current = true;
            return;
        }
        const dpr = window.devicePixelRatio || 1;
        const offCtx = oc.getContext('2d');
        offCtx.save();
        offCtx.setTransform(
            dpr * zoomRef.current, 0,
            0, dpr * zoomRef.current,
            dpr * offsetRef.current.x,
            dpr * offsetRef.current.y
        );
        drawStroke(offCtx, stroke);
        offCtx.restore();
    }, []);

    // ==========================================
    // CANVAS SETUP & RESIZE
    // ==========================================
    const setupCanvas = useCallback(() => {
        const canvas = canvasRef.current;
        const container = containerRef.current;
        if (!canvas || !container) return;
        const dpr = window.devicePixelRatio || 1;
        const rect = container.getBoundingClientRect();
        const w = Math.floor(rect.width * dpr);
        const h = Math.floor(rect.height * dpr);
        // ResizeObserver potrafi tyknąć bez realnej zmiany rozmiaru; przypisanie
        // canvas.width czyści płótno, więc robimy to tylko gdy naprawdę trzeba.
        if (canvas.width === w && canvas.height === h) return;
        canvas.style.width = `${rect.width}px`;
        canvas.style.height = `${rect.height}px`;
        canvas.width = w;
        canvas.height = h;
        canvasRectRef.current = canvas.getBoundingClientRect();
        invalidateOffscreen();
        renderCanvas();
    }, []);
    // ==========================================
    // RENDERING (with offscreen canvas cache)
    //
    // Kluczowa optymalizacja: ukończone stroke'i renderujemy
    // na offscreen canvas i cache'ujemy. Na głównym canvasie
    // rysujemy tylko cache + aktualny stroke.
    // Dzięki temu podczas rysowania (pointerMove @120Hz)
    // renderujemy tylko 1 stroke zamiast N.
    // ==========================================
    const renderCanvas = useCallback(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        const ctx = canvas.getContext('2d');
        const dpr = window.devicePixelRatio || 1;
        const currentZoom = zoomRef.current;
        const currentOffsetX = offsetRef.current.x;
        const currentOffsetY = offsetRef.current.y;

        // Sprawdź czy transform się zmienił od ostatniego renderowania offscreen
        const t = offscreenTransformRef.current;
        if (
            !offscreenDirtyRef.current &&
            (t.zoom !== currentZoom || t.offsetX !== currentOffsetX || t.offsetY !== currentOffsetY ||
             t.width !== canvas.width || t.height !== canvas.height)
        ) {
            offscreenDirtyRef.current = true;
        }

        // Rebuild offscreen cache jeśli potrzeba
        if (offscreenDirtyRef.current) {
            if (!offscreenRef.current) {
                offscreenRef.current = document.createElement('canvas');
            }
            const oc = offscreenRef.current;
            // Resize offscreen tylko gdy wymiary się zmieniły
            if (oc.width !== canvas.width || oc.height !== canvas.height) {
                oc.width = canvas.width;
                oc.height = canvas.height;
            }
            const offCtx = oc.getContext('2d');
            offCtx.clearRect(0, 0, oc.width, oc.height);
            offCtx.save();
            offCtx.setTransform(
                dpr * currentZoom, 0,
                0, dpr * currentZoom,
                dpr * currentOffsetX,
                dpr * currentOffsetY
            );
            strokesRef.current.forEach(stroke => drawStroke(offCtx, stroke));
            offCtx.restore();

            // Zapamiętaj transform
            offscreenTransformRef.current = {
                zoom: currentZoom,
                offsetX: currentOffsetX,
                offsetY: currentOffsetY,
                width: canvas.width,
                height: canvas.height,
            };
            offscreenDirtyRef.current = false;
        }

        // Główny canvas: cache + aktualny stroke
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        // Rysuj offscreen cache (wszystkie ukończone stroke'i)
        if (offscreenRef.current) {
            ctx.drawImage(offscreenRef.current, 0, 0);
        }
        // Rysuj tylko aktualny stroke na żywo
        if (currentStrokeRef.current) {
            ctx.save();
            ctx.setTransform(
                dpr * currentZoom, 0,
                0, dpr * currentZoom,
                dpr * currentOffsetX,
                dpr * currentOffsetY
            );
            drawStroke(ctx, currentStrokeRef.current);
            ctx.restore();
        }
    }, []);

    const scheduleRender = useCallback(() => {
        if (!rafRef.current) {
            rafRef.current = requestAnimationFrame(() => {
                rafRef.current = null;
                renderCanvas();
            });
        }
    }, [renderCanvas]);
    // ==========================================
    // UNDO / REDO / CLEAR
    // ==========================================
    const syncUndoRedoButtons = useCallback(() => {
        setCanUndo(undoStackRef.current.length > 0);
        setCanRedo(redoStackRef.current.length > 0);
    }, []);
    // Zacommitowane stroke'i nigdy nie są mutowane, więc wystarczy płytka kopia tablicy.
    // Deep clone (JSON round-trip) blokował główny wątek dokładnie w momencie oderwania rysika.
    const pushUndoState = useCallback(() => {
        undoStackRef.current.push(strokesRef.current.slice());
        if (undoStackRef.current.length > MAX_UNDO_STEPS) undoStackRef.current.shift();
        redoStackRef.current = [];
        syncUndoRedoButtons();
    }, [syncUndoRedoButtons]);
    const handleUndo = useCallback(() => {
        if (undoStackRef.current.length === 0) return;
        redoStackRef.current.push(strokesRef.current.slice());
        if (redoStackRef.current.length > MAX_UNDO_STEPS) redoStackRef.current.shift();
        strokesRef.current = undoStackRef.current.pop();
        syncUndoRedoButtons();
        invalidateOffscreen();
        scheduleRender();
    }, [scheduleRender, syncUndoRedoButtons, invalidateOffscreen]);
    const handleRedo = useCallback(() => {
        if (redoStackRef.current.length === 0) return;
        undoStackRef.current.push(strokesRef.current.slice());
        if (undoStackRef.current.length > MAX_UNDO_STEPS) undoStackRef.current.shift();
        strokesRef.current = redoStackRef.current.pop();
        syncUndoRedoButtons();
        invalidateOffscreen();
        scheduleRender();
    }, [scheduleRender, syncUndoRedoButtons, invalidateOffscreen]);
    const handleClear = useCallback(() => {
        if (strokesRef.current.length === 0) return;
        pushUndoState();
        strokesRef.current = [];
        invalidateOffscreen();
        scheduleRender();
    }, [pushUndoState, scheduleRender, invalidateOffscreen]);
    // ==========================================
    // INSERT COORDINATE SYSTEM
    // ==========================================
    const handleInsertCoordSystem = useCallback(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        const rect = canvas.getBoundingClientRect();
        const centerX = (rect.width / 2 - offsetRef.current.x) / zoomRef.current;
        const centerY = (rect.height / 2 - offsetRef.current.y) / zoomRef.current;
        const size = Math.min(rect.width, rect.height) * 0.6 / zoomRef.current;
        const sw = 1.5 / zoomRef.current;
        pushUndoState();
        strokesRef.current.push(...generateCoordinateSystem(centerX, centerY, size, sw));
        invalidateOffscreen();
        scheduleRender();
    }, [pushUndoState, scheduleRender, invalidateOffscreen]);
    // ==========================================
    // ZOOM
    // ==========================================
    const applyZoom = useCallback((newZoom, centerX, centerY) => {
        const clamped = Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, newZoom));
        const oldZoom = zoomRef.current;
        if (centerX !== undefined && centerY !== undefined) {
            offsetRef.current.x = centerX - (centerX - offsetRef.current.x) * (clamped / oldZoom);
            offsetRef.current.y = centerY - (centerY - offsetRef.current.y) * (clamped / oldZoom);
        }
        zoomRef.current = clamped;
        setZoom(clamped);
        // Transform zmieniony — renderCanvas() sam wykryje i zinwaliduje offscreen
        scheduleRender();
    }, [scheduleRender]);
    const handleZoomIn = useCallback(() => {
        const c = canvasRef.current; if (!c) return;
        const r = c.getBoundingClientRect();
        applyZoom(zoomRef.current + ZOOM_STEP, r.width / 2, r.height / 2);
    }, [applyZoom]);
    const handleZoomOut = useCallback(() => {
        const c = canvasRef.current; if (!c) return;
        const r = c.getBoundingClientRect();
        applyZoom(zoomRef.current - ZOOM_STEP, r.width / 2, r.height / 2);
    }, [applyZoom]);
    // ==========================================
    // COORDINATE CONVERSION
    // ==========================================
    const screenToPaper = useCallback((clientX, clientY) => {
        const rect = canvasRectRef.current;
        if (!rect) return { x: 0, y: 0 };
        return {
            x: (clientX - rect.left - offsetRef.current.x) / zoomRef.current,
            y: (clientY - rect.top - offsetRef.current.y) / zoomRef.current,
        };
    }, []);
    // ==========================================
    // LINE STRAIGHTENING (hold-to-snap)
    // ==========================================
    const clearHoldTimer = useCallback(() => {
        if (holdTimerRef.current) { clearTimeout(holdTimerRef.current); holdTimerRef.current = null; }
    }, []);
    const snapToStraightLine = useCallback(() => {
        const s = currentStrokeRef.current;
        if (!s || s.tool === TOOLS.ERASER || s.points.length < 3) return;
        const z = zoomRef.current;
        const first = s.points[0];
        const last = s.points[s.points.length - 1];
        const dx = (last.x - first.x) * z;
        const dy = (last.y - first.y) * z;
        const len = Math.sqrt(dx * dx + dy * dy);
        // Prostowanie ma sens tylko dla długich kresek (osie, odcinki).
        if (len < LINE_STRAIGHTEN_MIN_LENGTH_PX) return;
        // ...i tylko gdy kreska JUŻ była prawie prosta. Inaczej zawahanie ręki
        // w środku cyfry ścinało ją do odcinka.
        const maxDev = Math.min(LINE_STRAIGHTEN_MAX_DEVIATION_PX, len * LINE_STRAIGHTEN_MAX_DEVIATION_RATIO);
        for (const p of s.points) {
            const dev = Math.abs((p.x - first.x) * dy - (p.y - first.y) * dx) * z / len;
            if (dev > maxDev) return;
        }
        s.straightLine = true;
        scheduleRender();
    }, [scheduleRender]);
    const restartHoldTimer = useCallback(() => {
        clearHoldTimer();
        holdTimerRef.current = setTimeout(snapToStraightLine, LINE_STRAIGHTEN_HOLD_MS);
    }, [clearHoldTimer, snapToStraightLine]);

    // ==========================================
    // DODAWANIE PUNKTÓW DO AKTUALNEJ KRESKI
    // ==========================================
    const addStrokePoint = useCallback((clientX, clientY) => {
        const s = currentStrokeRef.current;
        if (!s) return;
        const raw = screenToPaper(clientX, clientY);
        if (s.straightLine) {
            s.points[s.points.length - 1] = raw;
            return;
        }
        const prev = s.points[s.points.length - 1];
        // Filtr wygładzający na wejściu — ciągnie punkt w stronę surowej próbki
        // zamiast brać ją wprost. Odsiewa drgania grotu, których krzywe już nie
        // wygładzą. Punkt odrzucony progiem nie blokuje kreski: kolejne próbki
        // dociągają punkt coraz bliżej surowej pozycji.
        const pt = prev ? {
            x: prev.x + (raw.x - prev.x) * INPUT_SMOOTHING_BETA,
            y: prev.y + (raw.y - prev.y) * INPUT_SMOOTHING_BETA,
        } : raw;
        const minD = MIN_POINT_DIST_PX / zoomRef.current;
        const dx = pt.x - prev.x, dy = pt.y - prev.y;
        if (dx * dx + dy * dy >= minD * minD) s.points.push(pt);
        if (lastMovePointRef.current) {
            const mx = pt.x - lastMovePointRef.current.x;
            const my = pt.y - lastMovePointRef.current.y;
            const still = LINE_STRAIGHTEN_STILL_RADIUS / zoomRef.current;
            if (mx * mx + my * my > still * still) {
                lastMovePointRef.current = pt;
                restartHoldTimer();
            }
        }
    }, [screenToPaper, restartHoldTimer]);

    // Rysik potrafi próbkować szybciej niż leci pointermove — coalesced events
    // odzyskują pominięte próbki, dzięki czemu szybkie kreski nie są ucinane.
    const addPointsFromEvent = useCallback((e) => {
        const native = e.nativeEvent || e;
        const coalesced = typeof native.getCoalescedEvents === 'function' ? native.getCoalescedEvents() : null;
        if (coalesced && coalesced.length > 0) {
            for (const ce of coalesced) addStrokePoint(ce.clientX, ce.clientY);
        } else {
            addStrokePoint(e.clientX, e.clientY);
        }
    }, [addStrokePoint]);

    // Punkt z pointerup — bez niego krótka kreska kończyła się jako pojedyncza kropka
    const addFinalPoint = useCallback((clientX, clientY) => {
        const s = currentStrokeRef.current;
        if (!s) return;
        const pt = screenToPaper(clientX, clientY);
        if (s.straightLine) {
            if (s.points.length > 1) s.points[s.points.length - 1] = pt;
            else s.points.push(pt);
            return;
        }
        const prev = s.points[s.points.length - 1];
        if (!prev || prev.x !== pt.x || prev.y !== pt.y) s.points.push(pt);
    }, [screenToPaper]);
    // ==========================================
    // STROKE PROPS
    // ==========================================
    const getStrokeProps = useCallback(() => {
        const { activeTool: tool, activeColor: color, penWidth: width } = toolRef.current;
        switch (tool) {
            case TOOLS.HIGHLIGHTER:
                return {
                    tool: TOOLS.HIGHLIGHTER, straightLine: false,
                    color: HIGHLIGHTER_COLORS.includes(color) ? color : HIGHLIGHTER_COLORS[0],
                    width: HIGHLIGHTER_WIDTH / zoomRef.current, opacity: HIGHLIGHTER_OPACITY,
                };
            case TOOLS.ERASER:
                return {
                    tool: TOOLS.ERASER, straightLine: false,
                    color: '#000', width: (ERASER_RADIUS * 2) / zoomRef.current, opacity: 1,
                };
            case TOOLS.PEN: default:
                return {
                    tool: TOOLS.PEN, straightLine: false,
                    color: PEN_COLORS.includes(color) ? color : PEN_COLORS[0],
                    width: width / zoomRef.current, opacity: 1,
                };
        }
    }, []);
    // ==========================================
    // GESTURE HELPERS
    // ==========================================
    // Gest wiąże DWA KONKRETNE kontakty (wcześniej brał dwa pierwsze z mapy, więc
    // przy leżącej dłoni liczył ją jako jeden z punktów szczypnięcia).
    const armTwoFingerGesture = useCallback((idA, idB) => {
        gestureRef.current = { a: idA, b: idB, active: false };
    }, []);
    // Baseline bierzemy dopiero przy aktywacji — dzięki temu płótno nie skacze.
    const activateGesture = useCallback(() => {
        const g = gestureRef.current;
        if (!g) return;
        const a = touchPointersRef.current.get(g.a);
        const b = touchPointersRef.current.get(g.b);
        if (!a || !b) return;
        const dx = b.x - a.x, dy = b.y - a.y;
        g.distance = Math.sqrt(dx * dx + dy * dy);
        g.zoom = zoomRef.current;
        g.offset = { ...offsetRef.current };
        g.midpoint = { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
        g.active = true;
    }, []);
    const applyPan = useCallback((clientX, clientY) => {
        const p = panRef.current;
        if (!p) return;
        offsetRef.current.x = p.offset.x + (clientX - p.startX);
        offsetRef.current.y = p.offset.y + (clientY - p.startY);
        scheduleRender();
    }, [scheduleRender]);
    // Kreska nigdy nie znika po cichu — każde zakończenie ją commituje.
    const finishStroke = useCallback(() => {
        clearHoldTimer();
        const stroke = currentStrokeRef.current;
        currentStrokeRef.current = null;
        lastMovePointRef.current = null;
        if (!stroke || stroke.points.length === 0) return;
        pushUndoState();
        strokesRef.current.push(stroke);
        appendStrokeToOffscreen(stroke);
    }, [clearHoldTimer, pushUndoState, appendStrokeToOffscreen]);
    // Jedyny przypadek celowego porzucenia: gest wystartował, więc kreska palcem
    // rozpoczęta przed nim była pomyłką.
    const discardStroke = useCallback(() => {
        clearHoldTimer();
        currentStrokeRef.current = null;
        lastMovePointRef.current = null;
    }, [clearHoldTimer]);
    // ==========================================
    // POINTER EVENTS
    //
    // Zasady, w tej kolejności:
    //  1. Dokładnie jeden wskaźnik jest aktywny. Zdarzenia z INNEGO pointerId
    //     nigdy nie kończą bieżącej kreski — spóźniony pointerup poprzedniej
    //     kreski zjadał następną (szybkie "=", drobne cyfry).
    //  2. Rysik zawsze wygrywa. Dotyk przy aktywnym rysiku to dłoń.
    //  3. Kreska nigdy nie znika po cichu — każde zakończenie ją commituje.
    //  4. Samoleczenie zgubionych zdarzeń: pen move z dotkniętym grotem bez
    //     aktywnej kreski = połknięty pointerdown → zaczynamy kreskę.
    //     Pen move bez nacisku z aktywną kreską = zgubiony pointerup → kończymy.
    // ==========================================
    // Safari potrafi podawać e.timeStamp w innej bazie niż performance.now()
    // (epoka vs czas od startu strony). Sprawdzamy to RAZ; jeśli baza się nie
    // zgadza, porównania czasu degradują się do no-opów zamiast blokować kreski.
    const timeBaseOkRef = useRef(null);
    const eventTime = useCallback((e) => {
        const now = performance.now();
        if (timeBaseOkRef.current === null) {
            timeBaseOkRef.current = typeof e.timeStamp === 'number' && Math.abs(e.timeStamp - now) < 5000;
        }
        return timeBaseOkRef.current ? e.timeStamp : now;
    }, []);
    // ŚWIADOMIE BEZ setPointerCapture. WebKit potrafi nie zwolnić przechwycenia
    // po szybkim oderwaniu rysika i połyka kolejny pointerdown (druga kreska "+"
    // nie powstawała wcale). Zamiast przechwytywania słuchamy move/up/cancel na
    // window — pointer nie ucieknie nam nawet poza canvasem.
    const logRef = useRef({ on: false, lines: [], moves: 0, timer: null });
    const [debugOn, setDebugOn] = useState(false);
    const [debugTick, setDebugTick] = useState(0);
    const logDbg = useCallback((line) => {
        const d = logRef.current;
        if (!d.on) return;
        d.lines.push(line);
        if (d.lines.length > 20) d.lines.shift();
        if (!d.timer) {
            d.timer = setTimeout(() => { d.timer = null; setDebugTick(t => t + 1); }, 120);
        }
    }, []);
    const evTag = useCallback((kind, e) => (
        `${kind} ${e.pointerType?.[0] ?? '?'}#${e.pointerId} b=${e.buttons}`
    ), []);
    const debugTapRef = useRef({ n: 0, t: 0 });
    const handleDebugTap = useCallback(() => {
        const now = Date.now();
        const s = debugTapRef.current;
        s.n = now - s.t < 700 ? s.n + 1 : 1;
        s.t = now;
        if (s.n < 5) return;
        s.n = 0;
        setDebugOn(prev => {
            const next = !prev;
            logRef.current.on = next;
            logRef.current.lines = next ? ['-- log zdarzen ON --'] : [];
            return next;
        });
        setDebugTick(t => t + 1);
    }, []);
    const beginStroke = useCallback((clientX, clientY) => {
        const pt = screenToPaper(clientX, clientY);
        currentStrokeRef.current = { ...getStrokeProps(), points: [pt] };
        lastMovePointRef.current = pt;
        restartHoldTimer();
        scheduleRender();
    }, [screenToPaper, getStrokeProps, restartHoldTimer, scheduleRender]);
    const beginPan = useCallback((clientX, clientY) => {
        panRef.current = { startX: clientX, startY: clientY, offset: { ...offsetRef.current } };
    }, []);
    // Przejęcie płótna przez rysik — używane i przy pointerdown, i przy odzysku.
    const takeOverWithPen = useCallback((e) => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        canvasRectRef.current = canvas.getBoundingClientRect();
        stylusDetectedRef.current = true;
        touchPointersRef.current.clear();
        gestureRef.current = null;
        finishStroke();
        panRef.current = null;
        activePointerRef.current = { id: e.pointerId, type: 'pen' };
        activeStartTimeRef.current = eventTime(e);
        penContactSeenRef.current = false;
        logRef.current.moves = 0;
        if (toolRef.current.activeTool === TOOLS.MOVE) beginPan(e.clientX, e.clientY);
        else beginStroke(e.clientX, e.clientY);
    }, [finishStroke, beginPan, beginStroke, eventTime]);
    const endActivePointer = useCallback((e, withFinalPoint, why) => {
        const pts = currentStrokeRef.current?.points.length ?? 0;
        if (withFinalPoint && currentStrokeRef.current) addFinalPoint(e.clientX, e.clientY);
        finishStroke();
        panRef.current = null;
        activePointerRef.current = null;
        if (e.pointerType === 'pen') penUpTimeRef.current = Math.max(penUpTimeRef.current, eventTime(e));
        logDbg(`  END(${why}) mv=${logRef.current.moves} pts=${pts}`);
        scheduleRender();
    }, [addFinalPoint, finishStroke, scheduleRender, eventTime, logDbg]);

    const handlePointerDown = useCallback((e) => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        logDbg(evTag('DOWN', e));
        e.preventDefault();
        // === PEN ===
        if (e.pointerType === 'pen') {
            lastPenEventTimeRef.current = eventTime(e);
            takeOverWithPen(e);
            return;
        }
        // === TOUCH ===
        // Rysik pisze w tej chwili → to dłoń. Rejestrujemy ją (potrzebne do liczenia
        // kontaktów), ale nic nią nie robimy. Gdy pointerup rysika zaginie, stan „pen"
        // zwietrzeje — inaczej gest przestawał działać do końca sesji.
        const now = eventTime(e);
        const penBusy = activePointerRef.current?.type === 'pen'
            && now - lastPenEventTimeRef.current < PEN_ACTIVE_TIMEOUT_MS;
        if (activePointerRef.current?.type === 'pen' && !penBusy) {
            activePointerRef.current = null;
        }
        canvasRectRef.current = canvas.getBoundingClientRect();
        touchPointersRef.current.set(e.pointerId, {
            x: e.clientX, y: e.clientY, startX: e.clientX, startY: e.clientY, t: now,
        });
        // Gest uzbrajamy tylko z DWÓCH kontaktów, które wylądowały blisko siebie
        // w czasie. Leżąca dłoń jest „stara", więc nigdy nie utworzy pary z palcem.
        if (!penBusy && !gestureRef.current) {
            for (const [id, p] of touchPointersRef.current) {
                if (id !== e.pointerId && now - p.t <= GESTURE_PAIR_WINDOW_MS) {
                    armTwoFingerGesture(id, e.pointerId);
                    logDbg(`  GEST uzbrojony ${id}+${e.pointerId}`);
                    break;
                }
            }
        }
        if (penBusy) return;                        // dłoń przy piszącym rysiku
        if (gestureRef.current) return;             // czekamy na ruch, nie rysujemy
        if (stylusDetectedRef.current) return;      // po wykryciu rysika palec nie rysuje
        activePointerRef.current = { id: e.pointerId, type: 'touch' };
        activeStartTimeRef.current = now;
        logRef.current.moves = 0;
        if (toolRef.current.activeTool === TOOLS.MOVE) beginPan(e.clientX, e.clientY);
        else beginStroke(e.clientX, e.clientY);
    }, [takeOverWithPen, armTwoFingerGesture, beginPan, beginStroke, eventTime, logDbg, evTag]);

    const handlePointerMove = useCallback((e) => {
        const active = activePointerRef.current;
        // === PEN MOVE ===
        if (e.pointerType === 'pen') {
            lastPenEventTimeRef.current = eventTime(e);
            const inContact = (e.buttons & 1) !== 0;
            if (!active || active.id !== e.pointerId) {
                // Grot dotyka płótna, a my nie rysujemy → pointerdown został połknięty.
                // Warunek na czas odsiewa spóźnione zdarzenia sprzed oderwania.
                if (inContact && eventTime(e) > penUpTimeRef.current) {
                    logDbg(evTag('RCVR', e));
                    takeOverWithPen(e);
                }
                return;
            }
            if (inContact) penContactSeenRef.current = true;
            else if (penContactSeenRef.current && eventTime(e) >= activeStartTimeRef.current) {
                endActivePointer(e, false, 'b=0');   // zgubiony pointerup — domknij
                return;
            }
            logRef.current.moves++;
            if (panRef.current) { applyPan(e.clientX, e.clientY); return; }
            if (currentStrokeRef.current) { addPointsFromEvent(e); scheduleRender(); }
            return;
        }
        // === TOUCH MOVE ===
        // Aktualizujemy pozycję w miejscu — startX/startY/t muszą przetrwać,
        // bo na nich opiera się rozpoznanie gestu.
        const tp = touchPointersRef.current.get(e.pointerId);
        if (tp) { tp.x = e.clientX; tp.y = e.clientY; }
        const g = gestureRef.current;
        const a = g && touchPointersRef.current.get(g.a);
        const b = g && touchPointersRef.current.get(g.b);
        if (a && b) {
            if (!g.active) {
                // Dopiero realny ruch odróżnia szczypnięcie od nieruchomej dłoni
                const movedA = Math.hypot(a.x - a.startX, a.y - a.startY);
                const movedB = Math.hypot(b.x - b.startX, b.y - b.startY);
                if (movedA > GESTURE_ACTIVATE_PX || movedB > GESTURE_ACTIVATE_PX) {
                    discardStroke();                 // gdyby palec zdążył zacząć kreskę
                    panRef.current = null;
                    activePointerRef.current = null;
                    activateGesture();
                    logDbg('  GEST aktywny');
                    scheduleRender();
                }
            }
            if (g.active && g.distance > 0) {
                const dx = b.x - a.x, dy = b.y - a.y;
                const dist = Math.sqrt(dx * dx + dy * dy);
                const newZoom = Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, g.zoom * (dist / g.distance)));
                const mid = { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
                const ratio = newZoom / g.zoom;
                offsetRef.current.x = mid.x - (g.midpoint.x - g.offset.x) * ratio;
                offsetRef.current.y = mid.y - (g.midpoint.y - g.offset.y) * ratio;
                zoomRef.current = newZoom;
                setZoom(newZoom);
                scheduleRender();
                return;
            }
        }
        if (!active || active.id !== e.pointerId) return;
        logRef.current.moves++;
        if (panRef.current) { applyPan(e.clientX, e.clientY); return; }
        if (currentStrokeRef.current) { addPointsFromEvent(e); scheduleRender(); }
    }, [takeOverWithPen, endActivePointer, applyPan, addPointsFromEvent, scheduleRender,
        eventTime, logDbg, discardStroke, activateGesture, evTag]);

    // Gest rozpada się gdy odejdzie JEDEN Z JEGO kontaktów — nie gdy spadnie
    // ogólna liczba dotknięć (leżąca dłoń zliczała się do puli).
    const releaseGestureIfMember = useCallback((pointerId) => {
        const g = gestureRef.current;
        if (g && (g.a === pointerId || g.b === pointerId)) gestureRef.current = null;
    }, []);

    const handlePointerUp = useCallback((e) => {
        logDbg(evTag('UP  ', e));
        if (e.pointerType === 'pen') penUpTimeRef.current = Math.max(penUpTimeRef.current, eventTime(e));
        else touchPointersRef.current.delete(e.pointerId);
        releaseGestureIfMember(e.pointerId);
        const active = activePointerRef.current;
        // Obce zdarzenie NIE dotyka bieżącej kreski. Rysik zachowuje ten sam
        // pointerId między kreskami, więc dodatkowo odsiewamy zdarzenia sprzed
        // startu bieżącej kreski.
        if (!active || active.id !== e.pointerId) { logDbg('  (obcy up)'); return; }
        if (eventTime(e) < activeStartTimeRef.current) { logDbg('  (spozniony up)'); return; }
        endActivePointer(e, true, 'up');
    }, [endActivePointer, eventTime, logDbg, evTag, releaseGestureIfMember]);

    const handlePointerCancel = useCallback((e) => {
        logDbg(evTag('CANC', e));
        if (e.pointerType !== 'pen') touchPointersRef.current.delete(e.pointerId);
        releaseGestureIfMember(e.pointerId);
        const active = activePointerRef.current;
        if (!active || active.id !== e.pointerId) return;
        if (eventTime(e) < activeStartTimeRef.current) return;
        endActivePointer(e, false, 'cancel');   // commitujemy — nie gubimy atramentu
    }, [endActivePointer, eventTime, logDbg, evTag, releaseGestureIfMember]);
    // ==========================================
    // SAVE / LOAD
    // ==========================================
    const serializeStrokes = useCallback(() => ({
        v: 1,
        strokes: strokesRef.current.map(s => ({
            p: s.points.map(pt => [Math.round(pt.x * 10) / 10, Math.round(pt.y * 10) / 10]),
            c: s.color, w: s.width, o: s.opacity, t: s.tool,
            sl: s.straightLine || false,
        })),
    }), []);
    const deserializeStrokes = useCallback((data) => {
        if (!data || data.v !== 1 || !Array.isArray(data.strokes)) return [];
        return data.strokes.map(s => ({
            points: s.p.map(([x, y]) => ({ x, y })),
            color: s.c, width: s.w, opacity: s.o, tool: s.t,
            straightLine: s.sl || false,
        }));
    }, []);
    const handleSaveAndClose = useCallback(() => {
        onSave(problem.problem_id, serializeStrokes());
        onClose();
    }, [serializeStrokes, onSave, problem?.problem_id, onClose]);
    // ==========================================
    // LIFECYCLE
    // ==========================================
    useEffect(() => {
        if (!isOpen) return;
        zoomRef.current = 1;
        offsetRef.current = { x: 0, y: 0 };
        undoStackRef.current = [];
        redoStackRef.current = [];
        touchPointersRef.current.clear();
        activePointerRef.current = null;
        panRef.current = null;
        gestureRef.current = null;
        currentStrokeRef.current = null;
        stylusDetectedRef.current = false;
        penContactSeenRef.current = false;
        penUpTimeRef.current = -Infinity;
        activeStartTimeRef.current = -Infinity;
        lastMovePointRef.current = null;
        offscreenDirtyRef.current = true;
        setZoom(1);
        setCanUndo(false);
        setCanRedo(false);
        strokesRef.current = initialPaths?.v === 1 ? deserializeStrokes(initialPaths) : [];
        const timer = setTimeout(() => setupCanvas(), 50);
        return () => clearTimeout(timer);
    }, [isOpen, initialPaths, deserializeStrokes, setupCanvas]);
    useEffect(() => {
        if (!isOpen || !containerRef.current) return;
        const observer = new ResizeObserver(() => setupCanvas());
        observer.observe(containerRef.current);
        return () => observer.disconnect();
    }, [isOpen, setupCanvas]);
    useEffect(() => {
        if (!isOpen) return;
        let backButtonListener;
        // Naglowek jest bialy -> ciemne ikony statusbara (globalny config) sa ok, nic nie zmieniamy.
        const setup = async () => {
            if (Capacitor.isNativePlatform()) {
                backButtonListener = await App.addListener('backButton', () => handleSaveAndClose());
            }
        };
        const cleanup = () => {
            if (backButtonListener) backButtonListener.remove();
        };
        setup();
        return () => { cleanup(); };
    }, [isOpen, handleSaveAndClose]);
    useEffect(() => () => { if (rafRef.current) cancelAnimationFrame(rafRef.current); }, []);
    // Natywne listenery zamiast syntetycznych Reacta: potrzebujemy { passive: false }
    // (preventDefault na pointerdown blokuje własne gesty WebView na rysiku)
    // i niezależności od cyklu renderowania Reacta w trakcie pisania.
    useEffect(() => {
        const canvas = canvasEl;
        if (!canvas) return;
        const opts = { passive: false };
        const prevent = (e) => e.preventDefault();
        // pointerdown na canvasie; reszta na window, bo nie używamy
        // setPointerCapture — inaczej zgubilibyśmy ruch poza płótnem.
        canvas.addEventListener('pointerdown', handlePointerDown, opts);
        window.addEventListener('pointermove', handlePointerMove, opts);
        window.addEventListener('pointerup', handlePointerUp, opts);
        window.addEventListener('pointercancel', handlePointerCancel, opts);
        canvas.addEventListener('contextmenu', prevent);
        // iOS: rozpoznawacze gestów WKWebView chodzą po zdarzeniach touch, nie
        // pointer. Bez tego preventDefault Safari potrafi połknąć całą sekwencję.
        canvas.addEventListener('touchstart', prevent, opts);
        canvas.addEventListener('touchmove', prevent, opts);
        canvas.addEventListener('gesturestart', prevent, opts);
        canvas.addEventListener('gesturechange', prevent, opts);
        return () => {
            canvas.removeEventListener('pointerdown', handlePointerDown);
            window.removeEventListener('pointermove', handlePointerMove);
            window.removeEventListener('pointerup', handlePointerUp);
            window.removeEventListener('pointercancel', handlePointerCancel);
            canvas.removeEventListener('contextmenu', prevent);
            canvas.removeEventListener('touchstart', prevent);
            canvas.removeEventListener('touchmove', prevent);
            canvas.removeEventListener('gesturestart', prevent);
            canvas.removeEventListener('gesturechange', prevent);
        };
    }, [canvasEl, handlePointerDown, handlePointerMove, handlePointerUp, handlePointerCancel]);
    useEffect(() => {
        if (!isOpen) return;
        const style = document.createElement('style');
        style.id = 'scratchpad-body-lock';
        style.textContent = `
            body > *:not([data-scratchpad]) {
                pointer-events: none !important;
                user-select: none !important;
                -webkit-user-select: none !important;
                -webkit-touch-callout: none !important;
            }
        `;
        document.head.appendChild(style);
        const prevOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        return () => {
            const el = document.getElementById('scratchpad-body-lock');
            if (el) el.remove();
            document.body.style.overflow = prevOverflow;
        };
    }, [isOpen]);
    // ==========================================
    // RENDER
    // ==========================================
    return ReactDOM.createPortal(
        <AnimatePresence>
          {isOpen && (
            <>
              <motion.div
                key="scratchpad-backdrop"
                {...getSheetBackdropMotion()}
                className="fixed inset-0 z-[99998] bg-[#233855]/80 backdrop-blur-sm"
              />
              <motion.div
                key="scratchpad-panel"
                {...getSheetPanelMotion(prefersReducedMotion, slideIn)}
                data-scratchpad="true"
                className="fixed inset-0 z-[99999] flex flex-col bg-white text-gray-800"
                style={{ overscrollBehavior: 'none', touchAction: 'none', userSelect: 'none', WebkitUserSelect: 'none' }}
              >
            <DrawingToolbar
                activeTool={activeTool} setActiveTool={setActiveTool}
                activeColor={activeColor} setActiveColor={setActiveColor}
                penWidth={penWidth} setPenWidth={setPenWidth}
                zoom={zoom} onZoomIn={handleZoomIn} onZoomOut={handleZoomOut}
                onUndo={handleUndo} onRedo={handleRedo} onClear={handleClear}
                canUndo={canUndo} canRedo={canRedo}
                onInsertCoordSystem={handleInsertCoordSystem}
                paddingTop={paddingTop} onSave={handleSaveAndClose}
                showCalculator={showCalculator} setShowCalculator={setShowCalculator}
                showBgImage={showBgImage} setShowBgImage={setShowBgImage}
                hasImage={!!problem.image_link}
                onDebugTap={handleDebugTap}
                highlightSave={!!coach}
            />
            <div className="flex-shrink-0 max-h-[35vh] overflow-y-auto border-b border-gray-200 bg-gray-50 p-3 shadow-inner z-20 relative"
                style={{ touchAction: 'pan-y' }}>
                <div className="prose prose-sm max-w-none mb-1 text-gray-900 leading-snug text-sm select-none pointer-events-none">
                    <RenderMarkdownWithLatex text={problem.question} />
                </div>
                {problem.linked_formulas && problem.linked_formulas.length > 0 && (
                    <div className="mt-1 border border-blue-200 rounded bg-white overflow-hidden">
                        <button onClick={() => setShowFormulas(!showFormulas)}
                            className="w-full flex justify-between items-center px-3 py-1.5 bg-blue-50/50 hover:bg-blue-100 transition-colors">
                            <span className="text-[10px] font-bold text-[#233855] uppercase tracking-wider">
                                Przydatne wzory ({problem.linked_formulas.length})
                            </span>
                            {showFormulas ? <FaChevronUp size={10} className="text-[#233855]" /> : <FaChevronDown size={10} className="text-[#233855]" />}
                        </button>
                        {showFormulas && (
                            <div className="p-2 border-t border-blue-100 space-y-2 bg-white">
                                {problem.linked_formulas.map((formula, idx) => (
                                    <div key={idx} className="text-sm">
                                        <div className="font-semibold text-gray-500 text-[10px] mb-0.5 uppercase tracking-wide">{formula.name}</div>
                                        <div className="bg-gray-50 px-2 py-1.5 rounded border border-gray-100 text-center text-gray-900 shadow-sm text-xs">
                                            <RenderMarkdownWithLatex text={formula.text || formula.content} />
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                )}
            </div>
            <div ref={containerRef} className="flex-grow relative bg-white overflow-hidden" style={{ touchAction: 'none' }}>
                {showCalculator && (
                    <Rnd default={{ x: typeof window !== 'undefined' && window.innerWidth > 400 ? window.innerWidth - 270 : 20, y: 20, width: 240, height: 340 }}
                        minWidth={200} minHeight={260} bounds="parent" lockAspectRatio={true}
                        dragHandleClassName="drag-handle" style={{ zIndex: 999 }}>
                        <SimpleCalculator onClose={() => setShowCalculator(false)} />
                    </Rnd>
                )}
                {problem.image_link && showBgImage && (
                    <div className="absolute inset-x-0 top-0 flex justify-center p-4 z-10 pointer-events-none">
                        <div className="bg-white p-2 rounded-lg shadow-[0_4px_15px_rgba(0,0,0,0.15)] border border-gray-200 inline-block max-w-full max-h-[60vh]">
                            <img src={problem.image_link.startsWith('http') ? problem.image_link : `${process.env.REACT_APP_STATIC_FILES_SOURCE || ''}/images/${problem.image_link}`}
                                alt="Podkład zadania" className="max-w-full max-h-[55vh] object-contain select-none" />
                        </div>
                    </div>
                )}
                <div className="absolute inset-0 pointer-events-none z-0"
                    style={{ backgroundImage: `url(${doodlePattern})`, backgroundRepeat: 'repeat', backgroundSize: '29px 29px', opacity: 1 }} />
                {/* Zdarzenia pointerów podpięte natywnie — patrz efekt wyżej */}
                <canvas ref={setCanvasRef} className="absolute inset-0 z-50"
                    style={{ cursor: activeTool === TOOLS.MOVE ? 'grab' : 'crosshair', touchAction: 'none' }}
                />
                {debugOn && (
                    <div key={debugTick}
                        className="absolute bottom-0 inset-x-0 z-[60] max-h-[45%] overflow-hidden bg-black/85 text-[10px] leading-[1.3] font-mono text-green-300 p-2 pointer-events-none whitespace-pre-wrap">
                        {logRef.current.lines.join('\n')}
                    </div>
                )}
            </div>

            {/* DYMEK SAMOUCZKA — kolejny krok prowadzenia, nie pasek na dole:
                zakotwiczony pod przyciskiem ZAPISZ, ktory jednoczesnie swieci. */}
            {coach && (
                <TutorialCoachmark
                    targetSelector='[data-tut="scratchpad-save"]'
                    title={coach.title}
                    body={coach.body}
                    stepIndex={coach.stepIndex}
                    stepCount={coach.stepCount}
                    zClass="z-[100000]"
                    fallbackBottom={`calc(1.5rem + ${paddingBottom})`}
                />
            )}
              </motion.div>
            </>
          )}
        </AnimatePresence>,
        document.body
    );
};
export default ScratchpadModal;